import React, { useEffect, useState } from "react";
import { Container, Form, Button, Alert, Spinner } from "react-bootstrap";
import { userActions } from "../actions/user.actions";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import PasswordInput from "./common/PasswordInput";
import { oktaDefaultConfig } from "../okta/oktaDefaultConfig";
import { useOktaAuth } from "@okta/okta-react";
import { emailDomainAuthenticatorService } from "../services/emailDomainAuthenticator.service";
import armaninoLogo from "../assets/armanino-llp.png";
import authBackground from "../assets/auth-background.jpg";
import Utils from "../utils";

function Login({ onOktaDetected, oktaConfigUpdated }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const user = useSelector((state) => state.user);
  const { oktaAuth } = useOktaAuth();
  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get("returnUrl") ?? Utils.getHomeRoute();
  const returnTo = searchParams.get("returnTo");
  const mcpState = searchParams.get("state");

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // Step 1: Email, Step 2: Password, Step 3: MFA code validation
  const [email, setEmail] = useState("");
  const [keepMeSignedIn, setKeepMeSignedIn] = useState(false);
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [mfaCodeError, setMfaCodeError] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleNextStep = async () => {
    setSubmitted(true);
    setEmailError("");

    if (!email) {
      setEmailError("Please provide your email.");
      return;
    }

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    // Move to step 2 or redirect to correspondent authenticator
    setLoading(true);
    emailDomainAuthenticatorService
      .checkDomain({ emailDomain: email.split("@")[1] })
      .then(
        (response) => {
          switch (response.data.authenticatorType) {
            case "Okta":
              let oktaConfig = oktaDefaultConfig;
              oktaConfig.issuer = response.data.oktaIssuerUrl;
              oktaConfig.clientId = response.data.oktaClientId;
              localStorage.setItem("keepMeSignedIn", keepMeSignedIn.toString());
              if (returnTo) {
                sessionStorage.setItem("mcpOAuthState", JSON.stringify({ returnTo, mcpState }));
              }
              onOktaDetected(oktaConfig);
              break;
            case "AzureAD":
            case "EntraID":
              // Redirect to Azure AD / Microsoft Entra ID (async IIFE)
              (async () => {
                const tenantId = response.data.azureAdTenantId;
                const clientId = response.data.azureAdClientId;
                const redirectUri = `${window.location.origin}/login/callback`;
                const nonce = Array.from(crypto.getRandomValues(new Uint8Array(16)))
                  .map((b) => b.toString(16).padStart(2, "0"))
                  .join("");

                sessionStorage.setItem(`oauthState_${nonce}`, JSON.stringify({
                  email,
                  keepMeSignedIn,
                  redirectUrl
                }));
                if (returnTo) {
                  sessionStorage.setItem("mcpOAuthState", JSON.stringify({ returnTo, mcpState }));
                }

                const state = nonce;

                // Generate PKCE code verifier and challenge
                const generateCodeVerifier = () => {
                  const array = new Uint8Array(32);
                  window.crypto.getRandomValues(array);
                  return base64UrlEncode(array);
                };

                const base64UrlEncode = (buffer) => {
                  const base64 = btoa(String.fromCharCode(...buffer));
                  return base64
                    .replace(/\+/g, '-')
                    .replace(/\//g, '_')
                    .replace(/=/g, '');
                };

                const sha256 = async (plain) => {
                  const encoder = new TextEncoder();
                  const data = encoder.encode(plain);
                  return window.crypto.subtle.digest('SHA-256', data);
                };

                const generateCodeChallenge = async (verifier) => {
                  const hashed = await sha256(verifier);
                  return base64UrlEncode(new Uint8Array(hashed));
                };

                // Generate PKCE values
                const codeVerifier = generateCodeVerifier();
                const codeChallenge = await generateCodeChallenge(codeVerifier);

                // Build Azure AD authorization URL
                const authUrl = new URL(`https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize`);
                authUrl.searchParams.append('client_id', clientId);
                authUrl.searchParams.append('response_type', 'code');
                authUrl.searchParams.append('redirect_uri', redirectUri);
                authUrl.searchParams.append('response_mode', 'query');
                authUrl.searchParams.append('scope', 'openid profile email');
                authUrl.searchParams.append('state', state);
                authUrl.searchParams.append('login_hint', email);
                authUrl.searchParams.append('code_challenge', codeChallenge);
                authUrl.searchParams.append('code_challenge_method', 'S256');

                // Store config for callback (including code verifier for token exchange)
                const configToStore = {
                  tenantId,
                  clientId,
                  redirectUri,
                  codeVerifier
                };

                localStorage.setItem("azureAdConfig", JSON.stringify(configToStore));
                localStorage.setItem("keepMeSignedIn", keepMeSignedIn.toString());

                // Small delay to ensure localStorage persists before redirect
                setTimeout(() => {
                  window.location.href = authUrl.toString();
                }, 100);
              })();
              break;
            default:
              setStep(2);
              setLoading(false);
              break;
          }
        },
        () => {
          setStep(2);
          setLoading(false);
        }
      );
  };

  useEffect(() => {
    if (oktaConfigUpdated) {
      oktaAuth.signInWithRedirect({
        originalUri: redirectUrl,
        loginHint: email,
        prompt: "login",
      });
    }

    // eslint-disable-next-line
  }, [oktaConfigUpdated]);

  useEffect(() => {
    if (user.mfaPrompt) {
      setStep(3);
    }

    // eslint-disable-next-line
  }, [user.mfaPrompt]);

  const handleLogin = async () => {
    setPasswordError(false);

    if (!password) {
      setPasswordError(true);
      return;
    }

    dispatch(userActions.login(email, password));
  };

  const handleChangeEmail = () => {
    setEmail("");
    setStep(1);
    dispatch(userActions.clearLoginError());
  };

  const handleMfaValidation = async () => {
    setMfaCodeError(false);

    if (!mfaCode) {
      setMfaCodeError(true);
      return;
    }

    dispatch(userActions.validateMfaCode(email, mfaCode, keepMeSignedIn, returnTo, mcpState));
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
        if (step === 1) {
            handleNextStep();
        }
      else if (step === 2) {
        handleLogin();
      } else if (step === 3) {
        handleMfaValidation();
      }      
    }
  };

  return (
    <div className="centered-div auth-page-bg" style={{ backgroundImage: `url(${authBackground})` }}>
      <Container
        className="login-box bg-white p-5 rounded border border-secondary mt-5"
        onKeyDown={onKeyDown}
      >
        <div className="text-center mb-5">
          <img
            src={armaninoLogo}
            alt="Armanino"
            width={250}
          />
        </div>
        {user.loginFailed && (
          <Alert variant={"danger"}>The email or password is incorrect</Alert>
        )}
        {user.loginRateLimited && (
          <Alert variant={"danger"}>
            Too many failed attempts. Your account has been temporarily locked.
            Please try again in{" "}
            {user.loginRateLimitedRetryAfter
              ? `${user.loginRateLimitedRetryAfter} minutes`
              : "a few minutes"}{" "}
            or{" "}
            <Alert.Link href="/sign-in-help">reset your password</Alert.Link>.
          </Alert>
        )}
        {user.mfaFailed && (
          <Alert variant={"danger"}>The code entered is incorrect</Alert>
        )}
        {user.mfaRateLimited && (
          <Alert variant={"danger"}>
            Too many failed attempts. Your account has been temporarily locked.
            Please try again in{" "}
            {user.mfaRateLimitedRetryAfter
              ? `${user.mfaRateLimitedRetryAfter} minutes`
              : "a few minutes"}{" "}
            or{" "}
            <Alert.Link href="/sign-in-help">reset your password</Alert.Link>.
          </Alert>
        )}
        {step === 1 && (
          <>
            <div className="mb-3">
              Please enter your email address to continue.
            </div>
            <Form.Group controlId="formBasicEmail" className="mb-3">
              <Form.Control
                type="email"
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={submitted && emailError ? "is-invalid" : ""}
              />
              {emailError && (
                <div className="invalid-feedback">
                  <small>{emailError}</small>
                </div>
              )}
            </Form.Group>

            <Form.Group controlId="formKeepSignedIn" className="mb-3">
              <Form.Check
                type="checkbox"
                label="Keep me signed in"
                checked={keepMeSignedIn}
                onChange={(e) => setKeepMeSignedIn(e.target.checked)}
              />
            </Form.Group>

            <div className="d-grid gap-2 mb-3">
              <Button
                variant="primary"
                className="mt-3"
                size="lg"
                onClick={handleNextStep}
                disabled={loading}
              >
                {!loading ? <>Next</> : <Spinner size="sm" />}
              </Button>
            </div>
          </>
        )}
        {step === 2 && (
          <>
            <div className="mb-3">
              Enter your password for <strong>{email}</strong>{" "}
              <Link
                onClick={handleChangeEmail}
                className="text-decoration-none"
              >
                (Change email)
              </Link>
            </div>

            <Form.Group controlId="formBasicPassword" className="mb-3">
              <PasswordInput
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                additionalCss={passwordError ? "is-invalid" : ""}
              />
              {passwordError && (
                <div className="invalid-feedback">
                  <small>Please provide your password.</small>
                </div>
              )}
            </Form.Group>

            <div className="d-grid gap-2 mb-3">
              <Button
                variant="primary"
                className="mt-3"
                size="lg"
                onClick={handleLogin}
                disabled={user.loading}
              >
                {!user.loading ? <>Login</> : <Spinner size="sm" />}
              </Button>
            </div>
          </>
        )}
        {step === 3 && (
          <>
            <div className="mb-3">
              Please enter the code you received by email.
            </div>
            <Form.Group controlId="formBasicCode" className="mb-3">
              <Form.Control
                type="text"
                placeholder="Enter code"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                className={submitted && mfaCodeError ? "is-invalid" : ""}
              />
              {mfaCodeError && (
                <div className="invalid-feedback">
                  <small>Please enter the code</small>
                </div>
              )}
            </Form.Group>

            <div className="d-grid gap-2 mb-3">
              <Button
                variant="primary"
                className="mt-3"
                size="lg"
                onClick={handleMfaValidation}
                disabled={user.loading}
              >
                {!user.loading ? <>Validate Code</> : <Spinner size="sm" />}
              </Button>
            </div>
          </>
        )}
        {step !== 3 && (
          <div className="text-center">
            <Link to={"/sign-in-help"} className="text-decoration-none">
              Forgot your password?
            </Link>
          </div>
        )}
      </Container>
    </div>
  );
}

export default Login;
